import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, MapPin, ChevronRight, Mail, User } from 'lucide-react';
import { organizersAPI } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import ContactOrganizerModal from '../components/ContactOrganizerModal';

export default function OrganizerProfilePage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [organizer, setOrganizer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContact, setShowContact] = useState(false);

  useEffect(() => {
    organizersAPI.get(id)
      .then(r => setOrganizer(r.data))
      .catch(() => navigate('/explore'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-16 flex items-center justify-center">
        <div className="text-gray-400">{t('loading')}</div>
      </div>
    );
  }

  if (!organizer) return null;

  return (
    <div className="min-h-screen bg-[#050505] pt-16 page-enter" data-testid="organizer-profile-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-10">
          <div className="w-20 h-20 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0">
            {organizer.picture ? (
              <img src={organizer.picture} alt={organizer.name} className="w-full h-full object-cover" />
            ) : (
              <User size={32} className="text-gray-500" />
            )}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {organizer.name}
            </h1>
            <div className="flex items-center gap-3 mt-2 text-gray-400 text-sm">
              <span>{organizer.events_hosted} events hosted</span>
              {organizer.is_dj && (
                <span className="gold-badge text-[10px] px-2 py-0.5 rounded-sm">DJ</span>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowContact(true)}
            className="flex items-center gap-2 border border-gray-700 hover:border-red-500 text-white text-sm px-4 py-2 rounded transition-colors"
            data-testid="contact-organizer-btn"
          >
            <Mail size={14} /> Contact Organizer
          </button>
        </div>

        <h2 className="text-lg font-black uppercase text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Upcoming Events
        </h2>
        {organizer.upcoming_events.length === 0 ? (
          <p className="text-gray-500 text-sm">No upcoming events right now.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {organizer.upcoming_events.map(event => {
              const minPrice = event.ticket_types?.length ? Math.min(...event.ticket_types.map(t => t.price)) : 0;
              return (
                <Link key={event.event_id} to={`/events/${event.event_id}`} className="bg-[#0A0A0A] border border-gray-800 rounded-lg overflow-hidden flex flex-col hover:border-gray-600 transition-colors">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={event.image_url || 'https://images.unsplash.com/photo-1765278797923-10a027f5c69d?w=600&q=80'} alt={event.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-black uppercase text-sm text-white mb-2 line-clamp-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>{event.title}</h3>
                    <div className="flex items-center gap-1.5 text-gray-400 text-xs mb-1">
                      <Calendar size={11} /> <span>{event.date}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-400 text-xs mb-3">
                      <MapPin size={11} /> <span className="truncate">{event.city}</span>
                    </div>
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-800">
                      <span className="text-white font-bold text-sm">{minPrice === 0 ? 'Free' : `From ${format(minPrice)}`}</span>
                      <ChevronRight size={14} className="text-gray-500" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {showContact && (
        <ContactOrganizerModal
          organizerId={organizer.user_id}
          organizerName={organizer.name}
          onClose={() => setShowContact(false)}
        />
      )}
    </div>
  );
}
